import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitReveal } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { usePrefersReducedMotion } from "../lib/useReducedMotion";

gsap.registerPlugin(ScrollTrigger);

// 35-YIL — vertical scroll converts to horizontal timeline (pinned).
export function Timeline() {
  const { t } = useLanguage();
  const reduced = usePrefersReducedMotion();
  const wrapRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    if (reduced || !trackRef.current || !wrapRef.current) return;
    const track = trackRef.current;
    const getScroll = () => track.scrollWidth - window.innerWidth;
    const tween = gsap.to(track, {
      x: () => -getScroll(),
      ease: "none",
      scrollTrigger: {
        trigger: wrapRef.current,
        start: "top top",
        end: () => "+=" + getScroll(),
        pin: true,
        scrub: 1,
        invalidateOnRefresh: true,
      },
    });
    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [reduced]);

  const milestones = t.timeline.milestones;

  return (
    <section ref={wrapRef} className="relative overflow-hidden bg-[#0F1115]" data-testid="timeline-section">
      <div className="pointer-events-none absolute left-6 top-24 z-10 md:left-12">
        <p className="heading text-sm not-italic tracking-[0.3em] text-hars-orange">{t.timeline.kicker}</p>
        <SplitReveal text={t.timeline.title} className="heading mt-3 max-w-md text-4xl text-white md:text-5xl" />
      </div>

      <div
        ref={trackRef}
        className={`flex h-screen items-center gap-6 px-6 md:px-12 ${
          reduced ? "overflow-x-auto" : "w-max"
        }`}
        style={{ willChange: "transform" }}
      >
        <div className="w-[70vw] shrink-0 md:w-[30vw]" />
        {milestones.map((m, i) => (
          <div
            key={i}
            data-testid={`timeline-node-${i}`}
            className="relative flex h-[52vh] w-[80vw] shrink-0 flex-col justify-end rounded-3xl border border-white/10 bg-[#08090B] p-8 md:w-[34vw]"
          >
            <span className="heading absolute -top-2 right-6 text-[9rem] leading-none text-white/[0.04]">{m.year}</span>
            <div className="mb-4 h-3 w-3 rounded-full bg-hars-orange shadow-[0_0_20px_rgba(240,116,42,0.8)]" />
            <p className="heading text-3xl text-hars-orange md:text-4xl">{m.year}</p>
            <p className="mt-3 max-w-xs font-inter text-sm text-hars-muted md:text-base">{m.text}</p>
          </div>
        ))}
        {/* Big 35 finale */}
        <div className="flex h-[52vh] w-[80vw] shrink-0 flex-col items-center justify-center rounded-3xl border border-hars-orange/30 bg-gradient-to-b from-hars-orange/10 to-transparent md:w-[34vw]">
          <span className="heading text-[14rem] leading-none text-hars-orange">35</span>
          <p className="heading text-sm not-italic tracking-[0.3em] text-white">{t.timeline.countLabel}</p>
        </div>
        <div className="w-[10vw] shrink-0" />
      </div>
    </section>
  );
}
